import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AddressInput from 'components/field/AddressInput';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { AddImportOrderModal } from 'pages/ImportOrders/AddImportOrderModal';
import ImportOrderVerifyModal from 'pages/ImportOrders/ImportOrderVerifyModal';
import BoxRow from 'pages/Shipments/ActionForms/CreateShipment/Components/BoxRow';
import AddOrderForm from 'pages/Shipments/Shipments/OrderForm/AddOrderForm';

import { AUTOCOMPLETE, ORDERS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faChevronRight, faSave } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ShippingInformation.styled';

const colSpan = {
	lg: 4,
	md: 12
};

const isIDRequiredCountry = (originCountry, destinationCountry) => {
	const ID_REQUIRED_COUNTRIES = ['QA', 'OM', 'KW'];
	return ID_REQUIRED_COUNTRIES.includes(destinationCountry) && originCountry !== destinationCountry;
};

const ShippingInformation = ({
	setDcCompanyData,
	isContinuesAction,
	setIsContinuesAction,
	disabledSelectOrder,
	shippingInformationCardRef,
	tableRef,
	setIsIdVerificationRequired,
	showBackButton,
	setVolumetricWeight,
	setClientCRDocumentStatus,
	isImportOrder,
	isQuickShipment
}) => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { track } = useAppTracking();
	const { Country } = useLocale();
	const { showError, showInfo } = useAlert();
	const navigate = useNavigate();
	const isTablet = useMediaQuery(Breakpoints.tablet);

	const {
		defaultBox: defaultBoxState,
		planName = null,
		firstName,
		lastName,
		email,
		phone,
		localization,
		ID
	} = useSelector((state) => state.user);

	const defaultBox = defaultBoxState
		? {
				ID: defaultBoxState.id,
				width: defaultBoxState.width,
				height: defaultBoxState.height,
				length: defaultBoxState.length,
				name: defaultBoxState.boxName,
				weight: 1
			}
		: { width: 10, height: 10, length: 10, weight: 1 };

	const [loading, setLoading] = useState(null);
	const { generalSettings, clientCards } = useSelector((state) => state?.user);
	const isNotCRDocVerified = generalSettings?.CRDocStatus !== 'VERIFIED';
	const noSavedCard = clientCards ? clientCards.length === 0 : true;

	const { control, setValue, watch, handleSubmit, setError } = useFormContext();

	const editMode = watch('editMode');

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'boxes'
	});

	const orderAutocompleteColumns = useMemo(() => [{ accessor: 'orderID', width: 200 }], []);
	const orderAutocompleteLabels = useMemo(() => ['orderID'], []);

	const onSubmit = async (formData) => {
		setLoading(true);
		if (formData?.paymentMethod && formData?.paymentMethod.toLowerCase() === 'cod' && !formData?.totalDue) {
			showError({
				header: formData.ID,
				message: 'error.codTotalDueNotExists'
			});
			setLoading(false);
			return;
		}

		await axios
			.post(PRICE_CONFIGURATIONS.GET_PRICE_LIST, {
				otoId: formData.orderID ?? undefined,
				calculationData: formData.orderID
					? {
							boxes: formData.boxes
						}
					: {
							boxes: formData.boxes,
							originAddress: {
								city: formData.originAddress.cityName,
								district: formData.originAddress.districtName,
								country: formData.originAddress.countryName,
								lat: formData.originAddress.lat,
								lon: formData.originAddress.lng
							},
							destinationAddress: {
								city: formData.destinationAddress.cityName,
								district: formData.destinationAddress.districtName,
								country: formData.destinationAddress.countryName,
								lat: formData.destinationAddress.lat,
								lon: formData.destinationAddress.lng
							}
						},
				...(isImportOrder ? { serviceType: 'IMPORT' } : {})
			})
			.then((response) => {
				const { data: responseData } = response;
				if (responseData.success) {
					isIDRequiredCountry(formData.originAddress.countryName, formData.destinationAddress.countryName)
						? setIsIdVerificationRequired(true)
						: setIsIdVerificationRequired(false);

					const { data } = responseData;
					data && setDcCompanyData(data);
					editMode && setValue('editMode', false);
					track(
						'shippingPricesCheck',
						{
							pricesCount: responseData.size,
							originCity: formData.originAddress.cityName,
							destinationCity: formData.destinationAddress.cityName,
							originCountry: formData.originAddress.countryName,
							destinationCountry: formData.destinationAddress.countryName,
							shipmentWeight: formData.boxes[0]?.weight,
							boxType: formData.boxes[0]?.name,
							volumetricWeight: responseData.volumetricWeight
						},
						['mixpanel']
					);
					setVolumetricWeight(responseData.volumetricWeight);
					setClientCRDocumentStatus(responseData.clientCRDocumentStatus);

					if (
						!isQuickShipment &&
						formData.orderID &&
						JSON.stringify(formData.initBoxes) !== JSON.stringify(formData.boxes)
					) {
						showInfo({
							header: 'priceCalculator.weigthAndBoxCountSavedHeader',
							message: 'priceCalculator.weigthAndBoxCountSavedMessage'
						});
					}
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						/* eslint-disable camelcase */
						event: 'price_calculator',
						first_name: firstName || '',
						last_name: lastName || '',
						phone: phone || '',
						email: email || '',
						plan: planName || '',
						country: localization?.label || '',
						userID: ID || ''
						/* eslint-enable camelcase */
					});
				} else {
					if (responseData.error === 'city_not_found') {
						setValue('error', responseData.error);
					} else {
						showError();
					}
					setDcCompanyData([]);
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				setDcCompanyData([]);
				showError();
				AppLogger.error(error);
			});

		tableRef?.collapseExpandedRows && tableRef.collapseExpandedRows();
		setLoading(false);
	};

	const handleAddBox = useCallback(() => {
		append(defaultBox);
		setTimeout(() => {
			shippingInformationCardRef?.scrollTo(0, shippingInformationCardRef?.scrollHeight);
		}, 300);
	}, [shippingInformationCardRef]);

	const onError = (errors) => {
		if (errors?.originAddress) {
			setError('originAddress.text', {
				message: t('errorMsg.cityAndCountryRequired'),
				type: 'required'
			});
		}

		if (errors?.destinationAddress) {
			setError('destinationAddress.text', {
				message: t('errorMsg.cityAndCountryRequired'),
				type: 'required'
			});
		}

		AppLogger.log(errors);
	};

	const setOrderData = async (value, isFirstRender) => {
		setValue('orderID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
		setValue('ID', value?.orderID ?? null, { shouldValidate: true, shouldDirty: true });

		if (isImportOrder) {
			setValue(
				'originAddress',
				{
					text: t('country.CN'),
					countryName: 'CN'
				},
				{
					shouldValidate: true,
					shouldDirty: true
				}
			);
		} else {
			setValue(
				'originAddress',
				{
					text: value
						? [t(value?.originCity), t(`country.${value?.originCountry}`)]
								.filter((item) => !!item)
								.join(', ')
						: null,
					cityName: value?.originCity ?? null,
					countryName: value?.originCountry ?? null,
					zipCode: value?.originPostCode ?? null,
					lat: value?.originLat ?? null,
					lng: value?.originLon ?? null
				},
				{
					shouldValidate: true,
					shouldDirty: true
				}
			);
		}

		setValue(
			'destinationAddress',
			{
				text: value
					? [t(value?.destinationCity), t(`country.${value?.destinationCountry}`)]
							.filter((item) => !!item)
							.join(', ')
					: null,
				cityName: value?.destinationCity ?? null,
				countryName: value?.destinationCountry ?? null,
				zipCode: value?.destinationPostCode ?? null,
				lat: value?.destinationLat ?? null,
				lng: value?.destinationLon ?? null
			},
			{
				shouldValidate: true,
				shouldDirty: true
			}
		);

		setValue('paymentMethod', value?.paymentMethod ?? null, {
			shouldValidate: true,
			shouldDirty: true
		});

		setValue('totalDue', value?.totalDue ?? null, {
			shouldValidate: true,
			shouldDirty: true
		});

		if (isContinuesAction) {
			setIsContinuesAction(false);
		}

		if (!isFirstRender || isContinuesAction) {
			await axios
				.post(ORDERS.GET_ORDER_BOXES_BY_ORDER_ID, { ID: value?.ID })
				.then((response) => {
					const { data, status } = response;
					if (status === 200) {
						if (data.length > 0) {
							setValue('boxes', data, {
								shouldValidate: true,
								shouldDirty: true
							});

							setValue('initBoxes', data, {
								shouldValidate: true,
								shouldDirty: true
							});
						} else if (value?.packageCount) {
							const boxes = [...Array(value?.packageCount).keys()]?.map((_, i) => ({
								...defaultBox,
								...(value?.weight > 0
									? i === 0
										? {
												weight: Number(
													(
														parseInt((value?.weight * 10) / value?.packageCount, 10) / 10 +
														((value?.weight * 10) % value?.packageCount) / 10
													).toFixed(1)
												)
											}
										: {
												weight: Number(
													(
														parseInt((value?.weight * 10) / value?.packageCount, 10) / 10
													).toFixed(1)
												)
											}
									: {})
							}));

							setValue('boxes', boxes, {
								shouldValidate: true,
								shouldDirty: true
							});

							setValue('initBoxes', boxes, {
								shouldValidate: true,
								shouldDirty: true
							});
						}
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					AppLogger.error(error);
				});
		}
	};

	const reloadPrices = useWatch({ control, name: 'reloadPrices' });

	useEffect(() => {
		if (reloadPrices) {
			handleSubmit(onSubmit, onError)();
			setValue('reloadPrices', false);
		}
	}, [reloadPrices]);

	useEffect(() => {
		if (isImportOrder && !watch('orderID')) {
			setValue('originAddress', {
				text: t('country.CN'),
				countryName: 'CN'
			});
			setValue('destinationAddress', {
				text: t('country.SA'),
				countryName: 'SA'
			});
		}
	}, [watch('orderID'), isImportOrder]);

	const handleCancelButtonClick = useCallback(() => {
		if (editMode) {
			setValue('editMode', false);
		} else if (showBackButton) {
			navigate(-1);
		} else {
			AppLogger.error("Unexpected 'cancel' button click in ShippingInformation component.");
		}
	}, [editMode]);

	const getAlertContent = useCallback(() => {
		if (isImportOrder) {
			return 'priceCalculator.importOrderAlert';
		}

		return 'priceCalculator.orderAlert';
	}, [isImportOrder]);

	return (
		<>
			<S.Container className={editMode ? 'editMode' : ''}>
				<S.FormContainer>
					<Alert showIcon variant="info" content={getAlertContent()} />
					<FormItem
						colSpan={colSpan}
						label="selectOrderOptional"
						toolTip="priceCalculator.selectOrderTooltip">
						<AutoComplete
							name="orderID"
							autoCompleteName={
								isImportOrder
									? AUTOCOMPLETE.GET_ESTIMATE_IMPORT_ORDERS
									: AUTOCOMPLETE.PRICE_CALCULATOR_ORDERS
							}
							placeholder="searchOrder"
							columns={orderAutocompleteColumns}
							selectionLabelAccessor={orderAutocompleteLabels}
							value={watch('orderID')}
							firstItem={{
								action: () =>
									NiceModal.show(
										isImportOrder
											? isNotCRDocVerified || noSavedCard
												? ImportOrderVerifyModal
												: AddImportOrderModal
											: AddOrderForm,
										{
											setOrder: (value) => {
												setValue(`orderID`, value, {
													shouldValidate: true,
													shouldDirty: true
												});
											}
										}
									),

								label: <Typography color="var(--blue)">addANewOrder</Typography>
							}}
							setValue={setOrderData}
							disabled={disabledSelectOrder}
						/>
					</FormItem>
					<FormItem colSpan={colSpan} label="origin" required toolTip="priceCalculator.originTooltip">
						<AddressInput
							name="originAddress"
							disabled={watch('orderID') !== null || isImportOrder}
							showButton={false}
							restrictions={
								planName !== 'EnterprisePackage'
									? {
											country: {
												isNotDifferent: planName === 'FreePackage',
												code: Country
											}
										}
									: undefined
							}
							showCountries={false}
						/>
					</FormItem>
					<FormItem
						colSpan={colSpan}
						label="destination"
						required
						toolTip="priceCalculator.destinationTooltip">
						<AddressInput
							name="destinationAddress"
							disabled={watch('orderID') !== null || isImportOrder}
							showButton={false}
							showCountries={false}
						/>
					</FormItem>
					<S.EmptyArea />
				</S.FormContainer>

				<S.BoxContainer>
					{fields.map((field, index) => (
						<React.Fragment key={`${field.id}${index}`}>
							<BoxRow
								index={index}
								remove={remove}
								fieldName="boxes"
								disabledDeleteButton={fields.length === 1}
							/>
							{isTablet && index !== fields.length - 1 && <S.BoxDividerLine />}
						</React.Fragment>
					))}
				</S.BoxContainer>

				<S.ButtonContainer>
					<Button label="addAnotherBox" variant="outline" icon={faPlus} onClick={handleAddBox} />
				</S.ButtonContainer>
			</S.Container>

			<S.ShippingInformationFooter>
				{(editMode || showBackButton) && (
					<Button
						variant="blackOutline"
						label="cancel"
						loading={loading}
						onClick={handleCancelButtonClick}
						css={css`
							text-decoration: underline;
							border: none !important;
						`}
					/>
				)}

				<Button
					label={editMode ? 'saveShippingDetails' : 'showShippingOptions'}
					icon={editMode ? faSave : faChevronRight}
					iconPos={editMode ? 'left' : 'right'}
					loading={loading}
					onClick={handleSubmit(onSubmit, onError)}
					disabled={watch('originAddress.text') === null || watch('destinationAddress.text') === null}
					disabledTooltip="priceCalculator.fillOriginAndDestination"
					css={css`
						body[dir='rtl'] & {
							> .button-icon {
								${!editMode ? 'transform: scaleX(-1);' : ''}
							}
						}
					`}
				/>
			</S.ShippingInformationFooter>
		</>
	);
};

export default ShippingInformation;
